import React, { useState, useRef, useEffect } from "react"
import PropTypes from "prop-types"
import classNames from "classnames"
import Icon from "@kit/design/icon"
import DropdownItem from "./dropdown-item"
import "./dropdown.css"

// TODO: This is a minimal implementation of our legacy button to fit the behavior of this dropdown.
// This whole Dropdown is deprecated, and should be removed when we have an accessible replacement
// in @kit/design
function DropdownButton(
  { children, icon, className = "", onClick, disabled, variant },
  ref
) {
  className = classNames(className, {
    "border border-gray-200 focus:outline-blue": variant !== "plain",
    "ck-button--plain focus:outline-none": variant === "plain",
    "cursor-not-allowed": disabled
  })

  return (
    <button
      ref={ref}
      onClick={onClick}
      disabled={disabled}
      className={className}
    >
      {icon && <Icon name={icon} />}
      {children}
    </button>
  )
}

// eslint-disable-next-line no-func-assign
DropdownButton = React.forwardRef(DropdownButton)

function Dropdown({
  className,
  buttonClassNames = "",
  iconClassNames = "",
  dropdownClassNames = "",
  size,
  variant,
  disabled,
  icon,
  title,
  children,
  position
}) {
  const [active, setActive] = useState(false)
  const [buttonHeight, setButtonHeight] = useState(0)
  const buttonContainer = useRef(null)
  const dropdown = useRef(null)

  const handleDocumentClick = event => {
    if (!dropdown.current.contains(event.target)) {
      setActive(false)
    }
  }

  useEffect(() => {
    document.addEventListener("mousedown", handleDocumentClick)

    return () => {
      document.removeEventListener("mousedown", handleDocumentClick)
    }
  })

  useEffect(() => {
    const buttonHeight = buttonContainer.current.offsetHeight
    setButtonHeight(buttonHeight)
  })

  const toggle = event => {
    event.preventDefault()
    event.stopPropagation()
    if (disabled) {
      return
    }
    setActive(!active)
  }

  const parentClassName = classNames("core-dropdown", className, {
    [`core-dropdown--${position}`]: position
  })

  const buttonClassName = classNames("core-dropdown__button", {
    [`${className}__button`]: className
  })

  return (
    <div ref={dropdown} data-active={active} className={parentClassName}>
      <div ref={buttonContainer}>
        <DropdownButton
          className={`${buttonClassName} ${buttonClassNames}`}
          size={size}
          variant={variant}
          onClick={toggle}
          disabled={disabled}
          aria-label="Toggle dropdown menu"
          type="button"
        >
          {title}
          {icon && <Icon name={icon} className={iconClassNames} />}
        </DropdownButton>
      </div>
      <div
        className={`${dropdownClassNames} core-dropdown__menu rounded`}
        style={{ top: buttonHeight }}
        onClick={() => setActive(false)}
        role="presentation"
      >
        {children}
      </div>
    </div>
  )
}

Dropdown.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]),
  className: PropTypes.string,
  buttonClassNames: PropTypes.string,
  iconClassNames: PropTypes.string,
  dropdownClassNames: PropTypes.string,
  icon: PropTypes.string,
  size: PropTypes.number,
  title: PropTypes.string,
  position: PropTypes.oneOf(["left", "right"]),
  variant: PropTypes.string,
  disabled: PropTypes.bool
}

Dropdown.Item = DropdownItem

export default Dropdown
